import React, { useState, useEffect } from "react";
import api from "../utils/api";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { CreditCard, Calendar, MapPin, CheckCircle2, Ticket, User, LogOut, Home } from "lucide-react";
import logo from "../images/2-removebg-preview.png";

// Media Assets
import img2 from "../images/about4.JPG";
import video1 from "../images/video.mp4";
import img3 from "../images/about1.JPG";

export default function Dashboard() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]); // Store array of purchased tickets
  const [activeMenu, setActiveMenu] = useState("tickets");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "" });

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name || "", phone: user.phone || "" });
    }
  }, [user]);

  useEffect(() => {
    const checkTicket = async () => {
      if (user) {
        try {
          const { data } = await api.get("/api/payment/check-ticket");
          if (data.hasTicket) setTickets(data.tickets || []);
        } catch (err) {
          console.error("Error checking ticket:", err);
        }
      } else if (!loading) {
        navigate("/booking");
      }
    };
    checkTicket();
  }, [user, loading, navigate]);

  const handlePayment = async (amount, ticketType) => {
    try {
      setSubmitting(true);
      if (!window.Cashfree) throw new Error("Payment SDK not loaded.");
      const cashfree = window.Cashfree({ mode: process.env.REACT_APP_CASHFREE_MODE || "production" });
      const { data } = await api.post("/api/payment/create-order", {
        amount: amount,
        ticketType: ticketType,
        customerName: user?.name,
        customerEmail: user?.email,
        customerPhone: user?.phone || "9999999999",
      });

      if (data.alreadyPurchased) {
        Swal.fire({
          icon: 'info',
          title: 'Already Purchased',
          text: `You already have a ${ticketType} ticket.`,
          background: '#0b0b0b',
          color: '#fff'
        });
        return; 
      }

      if (!data?.payment_session_id) throw new Error("Session creation failed.");
      cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message;
      Swal.fire({ icon: 'error', title: 'Payment Error', text: errorMsg, background: '#0b0b0b', color: '#fff' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-screen">Loading...</div>;

  return (
    <div className="bg-black text-white min-h-screen w-full flex flex-col lg:flex-row">
      <div className="flex-1 flex flex-col lg:flex-row w-full max-w-[1920px] mx-auto min-h-screen">
        
        {/* SIDEBAR */}
        <aside className="w-80 h-screen sticky top-0 bg-black border-r border-zinc-800 p-8 flex flex-col gap-10 hidden lg:flex">
          <div className="flex flex-col gap-2">
            <img src={logo} alt="Calyx" className="w-32" />
          </div>

          <div className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded-2xl border border-zinc-800">
            <div className="w-12 h-12 bg-[#CBA647] rounded-full flex items-center justify-center">
              <User size={24} className="text-black" />
            </div>
            <div className="flex flex-col overflow-hidden">
              <h3 className="text-white font-bold truncate">{user?.name}</h3>
              <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
            </div>
          </div>

          <nav className="flex flex-col gap-3">
            <button onClick={() => navigate("/")} className="flex items-center gap-3 p-4 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-xl transition-all">
              <Home size={18} /> <span className="font-medium">Back to Site</span>
            </button>
            <button 
              onClick={() => setActiveMenu("tickets")} 
              className={`flex items-center gap-3 p-4 rounded-xl transition-all ${activeMenu === "tickets" ? "bg-[#CBA647] text-black font-bold shadow-lg shadow-[#CBA647]/20" : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}
            >
              <Ticket size={18} /> <span className="font-medium">{tickets.length > 0 ? "My Tickets" : "Book Event"}</span>
            </button>
            <button 
              onClick={() => setActiveMenu("profile")} 
              className={`flex items-center gap-3 p-4 rounded-xl transition-all ${activeMenu === "profile" ? "bg-[#CBA647] text-black font-bold shadow-lg shadow-[#CBA647]/20" : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}
            >
              <User size={18} /> <span className="font-medium">My Account</span>
            </button>
            <button onClick={logout} className="flex items-center gap-3 p-4 text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all mt-auto">
              <LogOut size={18} /> <span className="font-medium">Logout</span>
            </button>
          </nav>
        </aside>

        {/* MAIN DASHBOARD CONTENT */}
        <main className="flex-1 p-4 lg:p-10 pb-28 lg:pb-10">
          {activeMenu === "tickets" ? (
            <div className="max-w-[1400px] mx-auto flex flex-col gap-10">
              
              <div className="w-full">
                <div className="flex flex-col lg:flex-row h-[250px] sm:h-[350px] lg:h-[500px] gap-4">
                  <div className="flex-[2] rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 shadow-2xl">
                    <video src={video1} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-[0.5] hidden lg:flex flex-col gap-4">
                    <img src={img2} alt="" className="w-full h-1/2 object-cover rounded-2xl border border-zinc-800" />
                    <img src={img3} alt="" className="w-full h-1/2 object-cover rounded-2xl border border-zinc-800" />
                  </div>
                </div>
              </div>

              {/* PHYSICAL SEMINAR SECTION */}
              <div className="flex flex-col items-center gap-12 py-10 w-full relative">
                <div className="text-center space-y-4">
                  <span className="text-[15px] text-[#CBA647] font-bold uppercase tracking-[0.6em] block">Physical Experience</span>
                  <h2 className="text-5xl lg:text-7xl font-black text-white uppercase tracking-tighter leading-none italic">Choose Your Tier</h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-transparent via-[#CBA647] to-transparent mx-auto" />
                  <div className="flex flex-wrap justify-center gap-4 mt-6">
                    <p className="flex items-center gap-2 px-5 py-3 bg-zinc-900/50 backdrop-blur-md rounded-full border border-white/10 text-xs text-zinc-400 font-medium">
                      <MapPin size={14} className="text-[#CBA647]" /> Location: Mumbai
                    </p>
                    <p className="flex items-center gap-2 px-5 py-3 bg-zinc-900/50 backdrop-blur-md rounded-full border border-white/10 text-xs text-zinc-400 font-medium">
                      <Ticket size={14} className="text-[#CBA647]" /> Venue: Taj Santacruz
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                  {[
                    { type: "Silver", price: "4,999", theme: "zinc-400", tag: "Essential", highlight: "bg-zinc-500/20" },
                    { type: "Gold", price: "7,499", theme: "gold-500", tag: "Preferred", highlight: "bg-gold-500/20" },
                    { type: "Platinum", price: "9,999", theme: "white", tag: "Elite VIP", highlight: "bg-white/20" }
                  ].map((tier, i) => {
                    const purchased = tickets.find(t => t.ticketType === tier.type);
                    return (
                      <div key={i} className={`relative group p-1 rounded-[3rem] transition-all duration-700 ${tier.type === 'Gold' ? 'bg-gradient-to-b from-[#CBA647]/30 to-transparent' : 'bg-white/5'}`}>
                        <div className="bg-zinc-950 rounded-[2.9rem] p-8 flex flex-col justify-between min-h-[420px] relative overflow-hidden">
                          {/* Ticket side notches */}
                          <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black border-r border-white/10" />
                          <div className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black border-l border-white/10" />
                          
                          {/* Top Detail */}
                          <div className="flex justify-between items-start mb-6">
                            <div className="space-y-1">
                              <span className="text-[8px] font-black tracking-[0.4em] text-zinc-500 uppercase">{tier.tag}</span>
                              <h4 className="text-2xl font-bold tracking-tight text-white uppercase italic">{tier.type}</h4>
                            </div>
                            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center border border-white/10 bg-white/5`}>
                              <Ticket size={20} className={tier.type === 'Gold' ? "text-[#CBA647]" : "text-white/40"} />
                            </div>
                          </div>

                          {/* Center Price / ID */}
                          <div className="flex flex-col justify-center mb-6">
                            {purchased ? (
                              <div className="space-y-4 animate-in fade-in zoom-in duration-700">
                                <div className="flex flex-col items-center gap-3">
                                  <CheckCircle2 size={40} className="text-emerald-500" />
                                  <span className="text-[9px] text-emerald-500 font-black tracking-[0.4em] uppercase">Check Your Mail</span>
                                </div>
                                <p className="text-sm text-zinc-400 font-medium text-center px-4">
                                  Your unique access code has been sent to your registered email address.
                                </p>
                                <p className="text-[10px] text-zinc-600 font-medium text-center">Please check inbox & spam folder</p>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <span className="text-[9px] text-zinc-600 font-bold tracking-[0.3em] uppercase">Single Admission</span>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-lg font-bold text-zinc-500 italic">₹</span>
                                  <h3 className="text-5xl font-black tracking-tighter text-white italic">{tier.price}</h3>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Footer Action */}
                          {!purchased ? (
                            <button 
                              onClick={() => handlePayment(parseInt(tier.price.replace(',', '')), tier.type)}
                              disabled={isSubmitting}
                              className={`w-full py-4 rounded-2xl font-black text-[10px] transition-all transform active:scale-95 flex items-center justify-center gap-3 uppercase tracking-[0.3em] ${tier.type === 'Gold' ? 'gold-gradient text-black shadow-2xl shadow-gold-900/40' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
                            >
                              <CreditCard size={18} /> {isSubmitting ? "WAIT..." : "Claim Seat"}
                            </button>
                          ) : (
                            <div className="w-full py-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 font-black text-[10px] uppercase tracking-[0.3em] flex items-center justify-center gap-2">
                              <CheckCircle2 size={18} /> Purchase Verified
                            </div>
                          )}

                          {/* Metallic Shimmer Effect on Hover */}
                          <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent pointer-events-none" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ONLINE MASTERCLASS SECTION */}
              <div className="flex flex-col justify-between items-center text-center gap-10 p-6 lg:p-10 bg-zinc-900/40 rounded-[32px] lg:rounded-[40px] border border-zinc-800 backdrop-blur-sm w-full">
                <div className="flex-1">
                  <h2 className="text-4xl lg:text-5xl font-bold text-[#CBA647] mb-6 drop-shadow-md text-center w-full uppercase tracking-tighter">Online Masterclass: Volume x Konstantin</h2>
                  
                  <div className="flex flex-wrap justify-center gap-4 w-full mb-10">
                    <p className="flex items-center gap-2 px-5 py-3 bg-zinc-900/50 backdrop-blur-md rounded-full border border-white/10 text-xs text-zinc-400 font-medium">
                      <Calendar size={14} className="text-[#CBA647]" /> 18 May 2026, 3 PM
                    </p>
                    <p className="flex items-center gap-2 px-5 py-3 bg-zinc-900/50 backdrop-blur-md rounded-full border border-white/10 text-xs text-zinc-400 font-medium">
                      <MapPin size={14} className="text-[#CBA647]" /> YouTube Live Masterclass
                    </p>
                  </div>
                </div>

                <div className="w-full lg:w-[600px] relative group">
                  <div className="absolute inset-0 bg-[#CBA647]/5 blur-[60px] group-hover:bg-[#CBA647]/10 transition-all duration-700" />
                  <div className="relative bg-zinc-900/40 p-1 rounded-[2.5rem] border border-white/5 backdrop-blur-xl">
                    {(() => {
                      const webinarTicket = tickets.find(t => t.ticketType === "Webinar");
                      if (webinarTicket) {
                        return (
                          <div className="p-10 rounded-[2.2rem] bg-black/40 border border-white/5 text-center">
                            <div className="flex flex-col items-center gap-3 mb-4">
                              <CheckCircle2 size={48} className="text-emerald-500" />
                              <p className="text-[10px] tracking-[0.4em] text-emerald-500 font-black uppercase opacity-60">Check Your Mail</p>
                            </div>
                            <p className="text-sm text-zinc-400 font-medium text-center px-4 mb-4">
                              Your unique access code has been sent to your registered email address.
                            </p>
                            <p className="text-[10px] text-zinc-600 font-medium text-center">Please check inbox & spam folder</p>
                          </div>
                        );
                      }
                      return (
                      <div className="p-10 flex flex-col items-center gap-8 text-center">
                        <div className="space-y-1">
                          <span className="text-[10px] text-zinc-500 font-bold tracking-widest uppercase">Admission Fee</span>
                          <h3 className="text-6xl font-black tracking-tighter text-white italic">₹ 999</h3>
                        </div>
                        <button 
                          onClick={() => handlePayment(999, "Webinar")} 
                          disabled={isSubmitting} 
                          className="w-full bg-[#CBA647] hover:brightness-110 disabled:bg-zinc-700 text-black py-5 rounded-2xl font-black text-sm transition-all transform active:scale-95 flex items-center justify-center gap-3 shadow-2xl shadow-gold-500/20 uppercase tracking-[0.2em]"
                        >
                          <CreditCard size={20} /> {isSubmitting ? "PROCESSING..." : "GET WEBINAR PASS"}
                        </button>
                      </div>
                    );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto py-10">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12">
                <h2 className="text-4xl font-bold text-white">Account Settings</h2>
                {!editMode ? (
                   <button onClick={() => setEditMode(true)} className="px-8 py-3 bg-zinc-900 text-[#CBA647] border border-[#CBA647]/30 rounded-xl hover:bg-[#CBA647] hover:text-black transition-all font-bold uppercase tracking-widest text-xs">Edit Profile</button>
                ) : (
                   <div className="flex gap-3">
                      <button onClick={() => setEditMode(false)} className="px-6 py-3 bg-zinc-900 text-white rounded-xl border border-zinc-800 hover:bg-zinc-800 transition-all font-medium">Cancel</button>
                      <button 
                        onClick={async () => {
                          setSubmitting(true);
                          const success = await updateProfile(formData.name, formData.phone);
                          if (success) setEditMode(false);
                          setSubmitting(false);
                        }} 
                        disabled={isSubmitting}
                        className="px-8 py-3 bg-[#CBA647] text-black rounded-xl font-bold hover:brightness-110 transition-all uppercase tracking-widest text-xs"
                      >
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </button>
                   </div>
                )}
              </div>

              <div className="bg-zinc-900/50 rounded-[40px] border border-zinc-800 overflow-hidden backdrop-blur-md">
                <div className="bg-gradient-to-r from-zinc-900 to-transparent p-10 flex items-center gap-8 border-b border-zinc-800/50">
                  <div className="w-24 h-24 bg-[#CBA647] rounded-3xl flex items-center justify-center rotate-3 shadow-2xl">
                    <User size={48} className="text-black" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-1">{user?.name}</h3>
                    <div className="flex items-center gap-2 text-[#CBA647] text-sm font-medium tracking-wide uppercase">
                      <CheckCircle2 size={16} /> OFFICIAL CALYX STUDENT
                    </div>
                  </div>
                </div>
                
                <div className="p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-3">
                    <span className="text-[10px] font-black text-zinc-500 tracking-[0.2em] uppercase">Full Name</span>
                    {editMode ? (
                      <input 
                        className="w-full bg-zinc-950 border border-zinc-800 text-white p-4 rounded-2xl focus:border-amber-500/50 outline-none transition-all"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    ) : (
                      <p className="text-xl font-medium text-white">{user?.name}</p>
                    )}
                  </div>
                  <div className="space-y-3 opacity-60">
                    <span className="text-[10px] font-black text-zinc-500 tracking-[0.2em] uppercase">Email Address</span>
                    <p className="text-xl font-medium text-zinc-400 italic">{user?.email}</p>
                  </div>
                  <div className="space-y-3">
                    <span className="text-[10px] font-black text-zinc-500 tracking-[0.2em] uppercase">Phone Number</span>
                    {editMode ? (
                      <input 
                        className="w-full bg-zinc-950 border border-zinc-800 text-white p-4 rounded-2xl focus:border-amber-500/50 outline-none transition-all"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    ) : (
                      <p className="text-xl font-medium text-white">{user?.phone || "Not Set"}</p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <span className="text-[10px] font-black text-zinc-500 tracking-[0.2em] uppercase">Account Status</span>
                    <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 rounded-full w-fit text-xs font-bold tracking-wider">
                      ACTIVE STUDENT
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* MOBILE BOTTOM NAVIGATION */}
        <nav className="fixed bottom-0 left-0 right-0 h-20 bg-zinc-950/90 backdrop-blur-xl border-t border-zinc-800 lg:hidden flex justify-around items-center px-4 z-50">
          <button onClick={() => navigate("/")} className="flex flex-col items-center gap-1 text-zinc-500 hover:text-white transition-all">
            <Home size={20} />
            <span className="text-[10px] font-bold">Home</span>
          </button>
          <button 
            onClick={() => setActiveMenu("tickets")} 
            className={`flex flex-col items-center gap-1 transition-all ${activeMenu === "tickets" ? "text-[#CBA647]" : "text-zinc-500"}`}
          >
            <Ticket size={20} />
            <span className="text-[10px] font-bold">Tickets</span>
          </button>
          <button 
            onClick={() => setActiveMenu("profile")} 
            className={`flex flex-col items-center gap-1 transition-all ${activeMenu === "profile" ? "text-[#CBA647]" : "text-zinc-500"}`}
          >
            <User size={20} />
            <span className="text-[10px] font-bold">Account</span>
          </button>
          <button onClick={logout} className="flex flex-col items-center gap-1 text-rose-500">
            <LogOut size={20} />
            <span className="text-[10px] font-bold">Exit</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
